import { useEffect, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { History, RotateCcw, GitCompare, X, Loader2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { WordDiff } from "../WordDiff";
import { htmlToText } from "../../lib/richtext";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

export function VersionsPanel({ workId, currentHtml, onRestore, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState([]); // ids, max 2
  const [docs, setDocs] = useState({}); // id -> content

  const load = async () => {
    if (!workId) return;
    setLoading(true);
    try { const { data } = await api.get(`/cv/works/${workId}/versions`); setVersions(data.versions || []); } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [workId]); // eslint-disable-line

  const fetchDoc = async (id) => {
    if (id === "current" || docs[id]) return;
    try { const { data } = await api.get(`/cv/works/${workId}/versions/${id}`); setDocs((d) => ({ ...d, [id]: data.content || "" })); } catch { /* */ }
  };
  const toggle = (id) => { fetchDoc(id); setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s.slice(-1), id])); };
  const restore = async (id) => {
    try { const { data } = await api.post(`/cv/works/${workId}/versions/${id}/restore`); onRestore(data.content); toast.success("Version restaurée"); load(); }
    catch (e) { toast.error(apiError(e, "Restauration impossible")); }
  };
  const contentOf = (id) => (id === "current" ? currentHtml : docs[id] || "");
  const labelOf = (id) => (id === "current" ? "Version actuelle (éditeur)" : (() => { const v = versions.find((x) => x.id === id); return v ? `${fmt(v.created_at)}${v.label ? ` — ${v.label}` : ""}` : ""; })());
  const [a, b] = sel;

  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="cv-versions">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-semibold text-navy"><History size={16} className="text-[#0E7C7B]" /> Historique des versions</p>
        <button onClick={onClose} className="text-slate-400 hover:text-navy" data-testid="cv-versions-close"><X size={18} /></button>
      </div>
      <p className="mt-1 text-xs text-slate-400">Une version est enregistrée à chaque Brouillon / Finaliser et après chaque action IA. Cochez deux versions pour les comparer côte à côte.</p>
      {!workId ? <p className="mt-3 text-sm text-slate-400">Enregistrez d'abord le document.</p> : loading ? <Loader2 size={16} className="mt-3 animate-spin text-slate-400" /> : (
        <ul className="mt-3 space-y-1.5" data-testid="cv-versions-list">
          <li className="flex items-center gap-3 rounded-lg bg-[#0E7C7B]/5 px-3 py-2 text-xs">
            <input type="checkbox" checked={sel.includes("current")} onChange={() => toggle("current")} data-testid="cv-version-check-current" />
            <span className="flex-1 font-medium text-navy">Version actuelle (éditeur)</span>
            <span className="text-slate-400">{htmlToText(currentHtml).length} car.</span>
          </li>
          {versions.length === 0 && <li className="text-xs text-slate-400">Aucune version archivée pour l'instant.</li>}
          {versions.map((v, i) => (
            <li key={v.id} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs" data-testid={`cv-version-${i}`}>
              <input type="checkbox" checked={sel.includes(v.id)} onChange={() => toggle(v.id)} data-testid={`cv-version-check-${i}`} />
              <span className="flex-1 min-w-0">
                <span className="block truncate font-medium text-navy">{fmt(v.created_at)}{v.label ? ` — ${v.label}` : ""}</span>
                <span className="text-slate-400">{v.status === "finalise" ? "Finalisé" : "Brouillon"} · {v.chars} car.</span>
              </span>
              <Button onClick={() => restore(v.id)} variant="outline" size="sm" className="h-7 rounded-full border-slate-300 text-[11px]" data-testid={`cv-version-restore-${i}`}><RotateCcw size={12} className="mr-1" /> Restaurer</Button>
            </li>
          ))}
        </ul>
      )}
      {a && b && (
        <div className="mt-4" data-testid="cv-versions-compare">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy"><GitCompare size={15} /> Comparaison</p>
          <div className="mt-2 grid gap-3 lg:grid-cols-2">
            {[a, b].map((id) => (
              <div key={id} className="rounded-xl border border-slate-200 p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{labelOf(id)}</p>
                <div className="cv-editor max-h-[420px] overflow-y-auto text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentOf(id)) }} />
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Différences ({labelOf(a)} → {labelOf(b)})</p>
            <WordDiff original={htmlToText(contentOf(a))} revised={htmlToText(contentOf(b))} className="text-sm text-slate-700" />
          </div>
        </div>
      )}
    </div>
  );
}
